import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import { Button } from "../components/ui/Button";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import {
  InlineRequestFieldManager, RequestFieldInput,
} from "../components/RequestFields";
import { RequestSection, requestMatchesSearch } from "../components/RequestList";
import { PrintableForms } from "../components/PrintableForms";
import { PrintRangeDialog } from "../components/PrintRangeDialog";
import { RequestDetailModal } from "../components/RequestDetailModal";
import { useEffectiveUser } from "../lib/auth";
import { can } from "../lib/permissions";

// Training requests live in the same time_off table (type =
// "training"). They get their own page + sidebar entry so a
// supervisor reviewing training separately from leave doesn't
// have to filter the combined list, and so officers submitting a
// training request find a dedicated landing spot.
export function TrainingPage() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const canPickAnyone = can.editRoster(me);
  const canManageFields = can.viewSettings(me);
  const start = ymd(addDays(new Date(), -30));
  const end = ymd(addDays(new Date(), 90));

  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const items = useQuery({
    queryKey: ["timeoff", start, end],
    queryFn: () => api.listTimeOff(start, end),
  });
  const trainingFields = useQuery({
    queryKey: ["requestFields", "training"],
    queryFn: () => api.listRequestFields("training"),
  });
  // Used by the detail modal to render reviewer names from the
  // user ids stored on l2_approved_by / reviewed_by.
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    enabled: canPickAnyone,
  });

  const empById = useMemo(
    () => Object.fromEntries((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );
  const shiftById = useMemo(
    () => Object.fromEntries((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );
  const usersById = useMemo(
    () => Object.fromEntries((users.data ?? []).map((u) => [u.id, u])),
    [users.data],
  );

  // Filter to training type. L3/L4 additionally see only their
  // own requests — they aren't supervisors, so the rest of the
  // agency's training queue isn't theirs to see.
  const visible = useMemo(() => {
    const all = (items.data ?? []).filter((t) => t.type === "training");
    if (canPickAnyone) return all;
    if (!me?.employeeId) return [];
    return all.filter((t) => t.employeeId === me.employeeId);
  }, [items.data, canPickAnyone, me?.employeeId]);

  // Controls: free-text search across employee / note / field values,
  // and per-officer / per-shift filters for supervisors scanning a
  // subset. Regular officers only see their own requests so the
  // employee filter is hidden for them. When a specific shift is
  // picked we render rows flat; otherwise rows are grouped under
  // each shift's header.
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState<string>("");
  const [shiftFilter, setShiftFilter] = useState<string>("");

  const filtered = useMemo(() => {
    return visible.filter((t) => {
      if (employeeFilter && t.employeeId !== employeeFilter) return false;
      if (shiftFilter) {
        const empShift = empById[t.employeeId]?.homeShiftId ?? "__unassigned__";
        if (shiftFilter !== empShift) return false;
      }
      return requestMatchesSearch(t, searchQuery, empById);
    });
  }, [visible, searchQuery, employeeFilter, shiftFilter, empById]);

  const filterActive =
    searchQuery.trim() !== "" || employeeFilter !== "" || shiftFilter !== "";

  // Split into what a supervisor still needs to act on (Active) and
  // everything already decided (Completed). Active stays in the
  // server's start-date-ascending order — pending items are easier
  // to scan chronologically. Completed flips to most-recent-first
  // so a long backlog doesn't push fresh decisions off screen.
  // Active = anything not yet finalised. That includes requests
  // sitting at l2_approved (the L2 partial-approval step that
  // training requests pass through on their way to a full
  // Owner / L1 approval), so they keep surfacing for the final
  // approver until someone closes them out.
  const active = useMemo(
    () => filtered.filter((t) => t.status === "requested" || t.status === "l2_approved"),
    [filtered],
  );
  const completed = useMemo(
    () => filtered
      .filter((t) => t.status === "approved" || t.status === "denied")
      .slice()
      .sort((a, b) => b.startDate.localeCompare(a.startDate)),
    [filtered],
  );

  // Detail modal — same row-click target for active and completed.
  // The modal's footer is what differs by status: Approve / Deny for
  // pending, Print / PDF for already-decided.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = useMemo(
    () => visible.find((t) => t.id === viewingId) ?? null,
    [visible, viewingId],
  );

  // Per-request print: when set, PrintableForms restricts itself to
  // just that one request and we trigger window.print() from an
  // effect once the DOM has re-rendered.
  const [printOnlyId, setPrintOnlyId] = useState<string | null>(null);
  useEffect(() => {
    if (!printOnlyId) return;
    const reset = () => setPrintOnlyId(null);
    window.addEventListener("afterprint", reset, { once: true });
    const t = setTimeout(() => window.print(), 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", reset);
    };
  }, [printOnlyId]);

  // Page-level "Print completed" gate. printRange null → normal
  // viewing; when set, requestsForPrint below is the date-filtered
  // subset and a useEffect fires window.print() on the next tick.
  const [printRangeOpen, setPrintRangeOpen] = useState(false);
  const [printRange, setPrintRange] = useState<{
    start: string; end: string;
  } | null>(null);
  const requestsForPrint = useMemo(() => {
    if (!printRange) return completed;
    return completed.filter(
      (t) => t.startDate >= printRange.start && t.startDate <= printRange.end,
    );
  }, [completed, printRange]);
  useEffect(() => {
    if (!printRange) return;
    const reset = () => setPrintRange(null);
    window.addEventListener("afterprint", reset, { once: true });
    const t = setTimeout(() => window.print(), 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", reset);
    };
  }, [printRange]);

  // Roster dropdown: active officers the user can see, sorted by
  // surname. Hidden for non-supervisors who only see their own rows.
  const employeeOptions = useMemo(
    () => (employees.data ?? [])
      .filter((e) => e.status !== "inactive")
      .slice()
      .sort((a, b) =>
        (a.lastName ?? "").localeCompare(b.lastName ?? "")
        || (a.firstName ?? "").localeCompare(b.firstName ?? "")),
    [employees.data],
  );
  // Shift dropdown: active shifts in their configured order, plus a
  // synthetic "Unassigned" bucket for officers without a home shift.
  const shiftOptions = useMemo(
    () => (shifts.data ?? [])
      .filter((s) => !s.archivedAt)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [shifts.data],
  );

  const [draft, setDraft] = useState<{
    employeeId: string;
    startDate: string;
    endDate: string;
    note: string;
    // Values for admin-configured fields, keyed by RequestField.key.
    metadata: Record<string, string | number | boolean | null>;
  } | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: () => api.requestTimeOff({
      employeeId: draft!.employeeId,
      startDate: draft!.startDate,
      endDate: draft!.endDate,
      type: "training",
      note: draft!.note || null,
      metadata: draft!.metadata,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timeoff"] });
      setDraft(null);
      setDraftError(null);
    },
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, approved }: { id: string; approved: boolean }) =>
      api.reviewTimeOff(id, approved),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timeoff"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
    },
  });

  function openDraft() {
    const initialMeta: Record<string, string | number | boolean | null> = {};
    for (const f of trainingFields.data ?? []) {
      initialMeta[f.key] = f.fieldType === "checkbox" ? false : "";
    }
    setDraft({
      employeeId: me?.employeeId ?? employees.data?.[0]?.id ?? "",
      startDate: ymd(new Date()),
      endDate: ymd(new Date()),
      note: "",
      metadata: initialMeta,
    });
    setDraftError(null);
  }

  // Sidebar shortcut: /training?openNew — open the draft once.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const state = location.state as { openNew?: true } | null;
    if (!state?.openNew) return;
    if (!me?.employeeId && (employees.data?.length ?? 0) === 0) return;
    openDraft();
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, me?.employeeId, employees.data]);

  return (
    <div className="p-4 md:p-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Training</h1>
          <p className="text-sm text-slate-500">
            {canPickAnyone
              ? "Training requests in the last 30 and next 90 days."
              : "Your training requests in the last 30 and next 90 days."}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            variant="secondary"
            onClick={() => setPrintRangeOpen(true)}
            disabled={completed.length === 0}
            title={completed.length === 0
              ? "Nothing to print yet — only approved or denied requests export to PDF."
              : "Pick a date range, then export each completed request as a printable form (one per page)."}
          >
            Print completed ({completed.length})
          </Button>
          <Button
            disabled={!me?.employeeId && !canPickAnyone}
            title={!me?.employeeId && !canPickAnyone
              ? "Your account isn't linked to an employee yet. Ask an admin."
              : undefined}
            onClick={openDraft}
          >
            + Training request
          </Button>
        </div>
      </header>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center print:hidden">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, note, or field value…"
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <select
          value={shiftFilter}
          onChange={(e) => setShiftFilter(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-44 focus:outline-none focus:ring-1 focus:ring-sky-400"
        >
          <option value="">All shifts</option>
          {shiftOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
          <option value="__unassigned__">Unassigned</option>
        </select>
        {canPickAnyone && (
          <select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-56 focus:outline-none focus:ring-1 focus:ring-sky-400"
          >
            <option value="">All officers</option>
            {employeeOptions.map((e) => (
              <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>
            ))}
          </select>
        )}
      </div>

      <div className="print:hidden">
        <RequestSection
          title="Active"
          count={active.length}
          emptyMessage={filterActive
            ? "No active training requests match."
            : "No pending training requests."}
          rows={active}
          empById={empById}
          fields={trainingFields.data ?? []}
          shiftById={shiftById}
          groupByShift={shiftFilter === ""}
          onView={(t) => setViewingId(t.id)}
        />

        <div className="mt-6">
          <RequestSection
            title="Completed"
            count={completed.length}
            emptyMessage={filterActive
              ? "No completed training requests match."
              : "No approved or denied training requests in this window."}
            rows={completed}
            empById={empById}
            fields={trainingFields.data ?? []}
            shiftById={shiftById}
            groupByShift={shiftFilter === ""}
            onView={(t) => setViewingId(t.id)}
          />
        </div>
      </div>

      <RequestDetailModal
        open={viewing !== null}
        request={viewing}
        employee={viewing ? empById[viewing.employeeId] : undefined}
        fields={trainingFields.data ?? []}
        kindLabel="Training"
        canReview={canPickAnyone}
        canFinalize={me?.level === "owner" || me?.level === "l1"}
        onReview={(id, approved) => reviewMut.mutate({ id, approved })}
        onPrint={(id) => { setViewingId(null); setPrintOnlyId(id); }}
        onClose={() => setViewingId(null)}
        usersById={usersById}
        empById={empById}
      />

      {/* Page-level "Print completed (N)" button uses onlyId=null,
        which prints every completed request. Per-request prints
        coming through the modal set onlyId to a single id. */}
      <PrintableForms
        agencyName={agency.data?.name ?? "Agency"}
        kindLabel="Training"
        requests={requestsForPrint}
        fields={trainingFields.data ?? []}
        empById={empById}
        usersById={usersById}
        onlyId={printOnlyId}
      />

      <PrintRangeDialog
        open={printRangeOpen}
        totalCount={completed.length}
        onConfirm={(range) => {
          setPrintRangeOpen(false);
          setPrintRange(range);
        }}
        onClose={() => setPrintRangeOpen(false)}
      />

      <Modal
        open={draft !== null}
        onClose={() => { setDraft(null); setDraftError(null); }}
        title="New training request"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setDraft(null); setDraftError(null); }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const missing = (trainingFields.data ?? []).filter((f) => {
                  if (!f.required) return false;
                  const v = draft?.metadata[f.key];
                  if (f.fieldType === "checkbox") return v !== true;
                  return v === "" || v === null || v === undefined;
                });
                if (missing.length > 0) {
                  setDraftError(`Required: ${missing.map((f) => f.label).join(", ")}`);
                  return;
                }
                setDraftError(null);
                createMut.mutate();
              }}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? "Saving…" : "Submit"}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <Field label="Employee">
              <Select
                value={draft.employeeId}
                onChange={(e) => setDraft({ ...draft, employeeId: e.target.value })}
                disabled={!canPickAnyone}
              >
                {(employees.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Start date">
                <Input type="date" value={draft.startDate}
                  onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
              </Field>
              <Field label="End date">
                <Input type="date" value={draft.endDate}
                  onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
              </Field>
            </div>

            {(trainingFields.data ?? []).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(trainingFields.data ?? []).map((f) => (
                  <RequestFieldInput
                    key={f.id}
                    field={f}
                    value={draft.metadata[f.key]}
                    onChange={(v) => setDraft({
                      ...draft,
                      metadata: { ...draft.metadata, [f.key]: v },
                    })}
                  />
                ))}
              </div>
            )}

            {canManageFields && (
              <div className="mt-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setManageOpen((v) => !v)}
                  className="text-xs font-medium text-sky-700 hover:text-sky-900"
                >
                  {manageOpen ? "Hide field settings" : "Manage fields…"}
                </button>
                {manageOpen && (
                  <InlineRequestFieldManager
                    scope="training"
                    fields={trainingFields.data ?? []}
                    onChanged={(updater) => {
                      setDraft((d) => d ? { ...d, metadata: updater(d.metadata) } : d);
                    }}
                  />
                )}
              </div>
            )}

            <Field label="Description">
              <Textarea value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                placeholder="Additional notes for the reviewer" />
            </Field>
            {draftError && (
              <div className="text-sm text-red-700">{draftError}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
